import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { Broadcast, Play, X, MagnifyingGlass, DownloadSimple, FilePdf } from "@phosphor-icons/react";

const TYPES = ["Truck", "Trailer", "Tractor", "Car", "Puller", "Tanker", "Bus"];
const EXP_COLS = [
  ["received_at", "Received"], ["vehicle_number", "Vehicle No."], ["imei", "IMEI"],
  ["protocol", "Protocol"], ["recognized", "Matched"], ["raw", "Raw Packet"],
];

export default function RawData() {
  const [data, setData] = useState({ packets: [], total: 0, matched: 0, unrecognized: 0 });
  const [live, setLive] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [customers, setCustomers] = useState([]);
  const [act, setAct] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildParams = () => {
    const p = { limit: 300 };
    if (q.trim()) p.q = q.trim();
    if (from) p.date_from = new Date(from).toISOString();
    if (to) p.date_to = new Date(to).toISOString();
    return p;
  };
  const load = () => api.get("/raw-data", { params: buildParams() }).then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data.customers)).catch(() => {});
    if (!live) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, q, from, to]);

  const clearFilters = () => { setQ(""); setFrom(""); setTo(""); };

  const rowsForExport = () => data.packets.map((p) => ({
    received_at: p.received_at, vehicle_number: p.vehicle_number || "", imei: p.imei,
    protocol: p.protocol, recognized: p.recognized ? "known" : "UNKNOWN", raw: p.raw,
  }));
  const exportCsv = () => {
    if (!data.packets.length) return toast.error("No packets to export");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [EXP_COLS.map((c) => c[1]).join(","),
      ...rowsForExport().map((r) => EXP_COLS.map((c) => esc(r[c[0]])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `raw-packets-${Date.now()}.csv`; a.click();
    toast.success("Excel (CSV) exported");
  };
  const exportPdf = () => {
    if (!data.packets.length) return toast.error("No packets to export");
    const w = window.open("", "_blank");
    const th = EXP_COLS.map((c) => `<th>${c[1]}</th>`).join("");
    const tr = rowsForExport().map((r) => `<tr>${EXP_COLS.map((c) => `<td>${r[c[0]] ?? ""}</td>`).join("")}</tr>`).join("");
    w.document.write(`<html><head><title>Raw Packets</title><style>body{font-family:Arial;padding:24px;color:#111}
      h1{font-size:18px}.meta{color:#666;font-size:12px;margin-bottom:12px}table{border-collapse:collapse;width:100%;font-size:10px}
      th,td{border:1px solid #ccc;padding:4px 6px;text-align:left}th{background:#f2f2f2}</style></head><body>
      <h1>Velvet AIS-140 — Raw Packet Log</h1>
      <div class="meta">${q ? `Search: ${q} · ` : ""}${from || "start"} → ${to || "now"} · ${data.packets.length} packets</div>
      <table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
    toast.success("Opening print / PDF view");
  };

  const openActivate = (r) => setAct({ imei: r.imei, recognized: r.recognized, vehicle_number: "", vehicle_type: "Truck", driver_name: "", custMode: "existing", customer_id: "", newName: "", newEmail: "", newPass: "" });
  const doActivate = async () => {
    if (!act.vehicle_number.trim()) return toast.error("Vehicle number required");
    const cust = {};
    if (act.custMode === "existing" && act.customer_id) cust.customer_id = act.customer_id;
    if (act.custMode === "new" && act.newName.trim()) cust.new_customer = { name: act.newName, email: act.newEmail, password: act.newPass };
    setBusy(true);
    try {
      if (act.recognized) {
        await api.post(`/inventory/${act.imei}/activate`, { vehicle_number: act.vehicle_number, vehicle_type: act.vehicle_type, driver_name: act.driver_name, ...cust });
      } else {
        await api.post(`/provision/batch`, { ...cust, vehicles: [{ imei: act.imei, vehicle_number: act.vehicle_number, vehicle_type: act.vehicle_type, driver_name: act.driver_name }] });
      }
      toast.success(`${act.vehicle_number.toUpperCase()} activated & tracking`);
      setAct(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const cols = [
    { key: "received_at", header: "Received", render: (r) => <span className="font-mono text-xs">{new Date(r.received_at).toLocaleString()}</span> },
    { key: "protocol", header: "Protocol", render: (r) => <Pill color="#0091e6">{r.protocol}</Pill> },
    { key: "vehicle_number", header: "Vehicle", render: (r) => r.vehicle_number ? <span className="font-semibold">{r.vehicle_number}</span> : <span className="text-muted-foreground text-xs">—</span> },
    { key: "imei", header: "IMEI", render: (r) => <span className="font-mono text-xs">{r.imei}</span> },
    { key: "recognized", header: "Matched", render: (r) => r.recognized ? <Pill color="#34C759">known</Pill> : <Pill color="#FF9500">UNKNOWN</Pill> },
    { key: "raw", header: "Raw Packet", render: (r) => <span className="font-mono text-xs text-muted-foreground break-all">{r.raw}</span> },
    { key: "act", header: "", render: (r) => <button data-testid={`raw-activate-${r.imei}`} onClick={() => openActivate(r)} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Play size={12} weight="fill" /> Activate</button> },
  ];

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Raw Packets" subtitle="Live raw packets hitting the server — search, inspect, export & activate devices"
        actions={<div className="flex items-center gap-2">
          <button data-testid="rawpkt-excel" onClick={exportCsv} className="flex items-center gap-1.5 bg-secondary text-white text-sm font-semibold px-3 py-2 rounded-sm"><DownloadSimple size={15} weight="bold" /> Excel</button>
          <button data-testid="rawpkt-pdf" onClick={exportPdf} className="flex items-center gap-1.5 bg-secondary text-white text-sm font-semibold px-3 py-2 rounded-sm"><FilePdf size={15} weight="bold" /> PDF</button>
          <button data-testid="raw-live-toggle" onClick={() => setLive((l) => !l)}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-sm ${live ? "bg-primary text-white" : "bg-secondary text-white"}`}>
            <Broadcast size={15} weight="fill" /> {live ? "Live" : "Paused"}
          </button>
        </div>} />

      {/* Filters */}
      <div className="bg-card border border-border rounded-sm p-3 flex flex-wrap items-end gap-3" data-testid="raw-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="label-caps">Search by vehicle number or IMEI</label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <input data-testid="raw-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. MH12AB1234 or 8654120345…"
              className="w-full bg-background border border-border rounded-sm pl-7 pr-3 py-1.5 text-sm focus:ring-1 focus:ring-primary focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="label-caps">From</label>
          <input data-testid="raw-from" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)}
            className="mt-1 block bg-background border border-border rounded-sm px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="label-caps">To</label>
          <input data-testid="raw-to" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)}
            className="mt-1 block bg-background border border-border rounded-sm px-2 py-1.5 text-sm" />
        </div>
        {(q || from || to) && <button data-testid="raw-clear" onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground py-2">Clear</button>}
        <span className="text-xs text-muted-foreground ml-auto">
          Showing <b className="text-foreground">{data.matched ?? data.packets.length}</b> · Total stored <b className="text-foreground">{data.total}</b> · Unknown IMEIs <b className="text-[#FF9500]">{data.unrecognized}</b>
        </span>
      </div>

      <p className="text-xs text-muted-foreground">Every packet a device sends to its brand TCP port is stored here, parsed by IMEI into vehicle telemetry. Hit <b>Activate</b> on any packet to put that device straight into service.</p>
      <DataTable testid="raw-data-table" columns={cols} rows={data.packets} empty="No raw packets for this filter — send one from a device or clear the search" />

      {act && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="raw-activate-modal">
          <div className="bg-card border border-border rounded-sm p-5 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-lg">Activate from Live</span>
              <button onClick={() => setAct(null)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground">IMEI <span className="font-mono text-foreground">{act.imei}</span> · {act.recognized ? "in inventory" : "new device — will be created"}. Map it to a vehicle to start tracking.</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="raw-act-vnum" value={act.vehicle_number} onChange={(e) => setAct({ ...act, vehicle_number: e.target.value })} placeholder="Vehicle number *" className="col-span-2 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <select value={act.vehicle_type} onChange={(e) => setAct({ ...act, vehicle_type: e.target.value })} className="bg-background border border-border rounded-sm px-2 py-2 text-sm">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <input value={act.driver_name} onChange={(e) => setAct({ ...act, driver_name: e.target.value })} placeholder="Driver (optional)" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setAct({ ...act, custMode: "existing" })} className={`text-xs font-semibold px-3 py-1.5 rounded-sm border ${act.custMode === "existing" ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground"}`}>Existing customer</button>
              <button onClick={() => setAct({ ...act, custMode: "new" })} className={`text-xs font-semibold px-3 py-1.5 rounded-sm border ${act.custMode === "new" ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground"}`}>New customer</button>
            </div>
            {act.custMode === "existing" ? (
              <select data-testid="raw-act-customer" value={act.customer_id} onChange={(e) => setAct({ ...act, customer_id: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
                <option value="">No customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
              </select>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <input value={act.newName} onChange={(e) => setAct({ ...act, newName: e.target.value })} placeholder="Name *" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
                <input value={act.newEmail} onChange={(e) => setAct({ ...act, newEmail: e.target.value })} placeholder="Login email" className="bg-background border border-border rounded-sm px-2 py-2 text-sm font-mono" />
                <input type="password" value={act.newPass} onChange={(e) => setAct({ ...act, newPass: e.target.value })} placeholder="Password" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setAct(null)} className="px-4 py-2 text-sm font-semibold border border-border rounded-sm">Cancel</button>
              <button data-testid="raw-act-submit" disabled={busy} onClick={doActivate} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-sm disabled:opacity-50">{busy ? "Activating…" : "Activate"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
